import React, { useMemo } from 'react';
import { Link } from 'react-router-dom';
import { ProgressBar } from 'react-bootstrap';

import AdvanceTable from '../../components/base/AdvanceTable';
import useAdvanceTable from '../../hooks/useAdvanceTable';
import AdvanceTableProvider from '../../providers/AdvanceTableProvider';
import Badge from '../../components/base/Badge';
import { useState } from 'react';
import EditPlanItemModal from '../sales-management/EditPlanItemModal';

const fmt = n => Number(n || 0).toLocaleString();
const safeArr = v => (Array.isArray(v) ? v : []);

const buildProductLabel = line => {
  const p = line?.product || {};
  const name = String(p.cableName || '')
    .trim()
    .toUpperCase();
  const n = Number(p.numberOfConductors || 0);
  const s = Number(p.section || 0);

  if (!name) return '—';

  if (p.hasMixedConductorSections) {
    const gy =
      p.greenYellowSection != null ? Number(p.greenYellowSection) : null;
    return `${name} ${n}×${s}${gy ? ` + GY ${gy}` : ''}${p.color ? ` (${p.color})` : ''}`;
  }

  return `${name} ${n}×${s}${p.color ? ` (${p.color})` : ''}`;
};

const getAllocBadge = statusRaw => {
  const s = String(statusRaw || 'NOT_ALLOCATED').toUpperCase();
  if (s === 'FULLY_ALLOCATED')
    return { label: 'Fully allocated', color: 'success' };
  if (s === 'PARTIALLY_ALLOCATED')
    return { label: 'Partially allocated', color: 'warning' };
  return { label: 'Not allocated', color: 'danger' };
};

/**
 * The fallback for lines the backend rollup has not enriched.
 *
 * Deliberately the same vocabulary as getReadinessBadge, and the same meaning:
 * READY means the cable exists. It used to mean "fully allocated", so a line
 * with everything promised and nothing made read "Ready" — and beside a
 * fulfilment figure of zero that is a badge telling somebody to go and ship
 * cable that has not been drawn yet.
 */
const getProductionBadge = (produced, allocated, total) => {
  if (total > 0 && produced >= total) return { label: 'Ready', color: 'success' };
  if (produced > 0) return { label: 'In production', color: 'info' };
  if (allocated > 0) return { label: 'Allocated', color: 'warning' };
  return { label: 'Not started', color: 'danger' };
};

// readiness from the backend's computed line.production.status (compute-on-read)
const getReadinessBadge = status => {
  switch (String(status || '')) {
    case 'produced':
      return { label: 'Ready', color: 'success' };
    case 'in_production':
      return { label: 'In production', color: 'info' };
    case 'allocated':
      return { label: 'Allocated', color: 'warning' };
    default:
      return { label: 'Not started', color: 'danger' };
  }
};

const buildLineRow = line => {
  const alloc = line?.allocation || {};
  const allocs = safeArr(alloc.allocations);
  const jobsCount = allocs.length;

  const total = Number(line?.totalOrderedLength || 0);
  const allocated = Number(line?.allocatedLength || 0);
  const produced = Number(line?.producedLength || 0);
  // meters reserved from finished stock count as fulfilled and don't need
  // producing → they drop out of "remaining to produce".
  const stockReserved = Number(line?.stockReservedLength || 0);
  const fulfilled = Math.min(total, produced + stockReserved);
  // What is left to LAUNCH — the column sits beside "Allocated (m)" and answers
  // the same question the order's own `remainingLength` does.
  //
  // `??`, not `||`. A fully allocated line has a remainder of exactly zero, and
  // `||` reads that as "missing" and falls through to the fallback — so the one
  // case where the answer is nothing was the one case that showed the whole
  // order still outstanding. The fallback also measured the wrong thing: what
  // was left to PRODUCE, in a column about what is left to promise.
  const remaining = Math.max(
    0,
    Number(line?.remainingLength ?? Math.max(0, total - allocated)) - stockReserved
  );

  const allocStatus = String(alloc.status || 'NOT_ALLOCATED');
  const allocBadge = getAllocBadge(allocStatus);

  // backend compute-on-read rollup (falls back to derived badge if absent)
  const prod = line?.production || null;
  const prodBadge = prod
    ? getReadinessBadge(prod.status)
    : getProductionBadge(produced, allocated, total);
  const fulfillmentPct = prod
    ? Number(prod.fulfillmentPct || 0)
    : total > 0
      ? Math.min(100, Math.round((fulfilled / total) * 100))
      : 0;
  const readyDrums = prod ? Number(prod.readyDrums || 0) : 0;
  const plannedDrums = prod ? Number(prod.plannedDrums || 0) : 0;

  const progress =
    total > 0 ? Math.min(100, Math.round((fulfilled / total) * 100)) : 0;

  // How much of the line is spoken for — a different question from how much of
  // it has been made, and the one the Allocation column is about.
  const allocationPct =
    total > 0 ? Math.min(100, Math.round((allocated / total) * 100)) : 0;

  const planTotal = Number(line?.deliveryPlan?.plannedTotal || 0);

  // delivery plan items with produced counts
  const dpItems = safeArr(line?.deliveryPlan?.items).map(item => {
    const kind = String(item.kind || '').toUpperCase();
    const qty = Number(item.qtyPlanned) || 0;
    const tokenCount = safeArr(item.tokenIds).length;
    // qtyProduced (from the compute-on-read enrichment) is the REAL produced
    // unit count. One coiling token carries many coils, so tokenIds.length
    // under-counts coils (2 tokens = 200 coils). Fall back to tokenCount only
    // when qtyProduced is absent.
    const producedQty =
      item.qtyProduced != null ? Number(item.qtyProduced) : tokenCount;
    const mat = item.drumMaterial || '';
    const dia = Number(item.drumDiameter) || 0;
    const len = Number(item.lengthPerUnit) || 0;

    const specs = kind === 'DRUM'
      ? [mat, dia ? `Ø${dia}` : ''].filter(Boolean).join(' ') || 'Drum'
      : 'Coil';

    const cancelledQty = Number(item.qtyCancelled) || 0;

    // The chip is clickable now, and the endpoint is addressed by itemId —
    // which the row used to drop on the floor.
    return { specs, qty, len, tokenCount, producedQty, cancelledQty, kind, itemId: item.itemId, raw: item };
  });

  return {
    lineId: line?.lineId || '—',
    // The drum capacity is a function of the CABLE, so the editor needs the
    // product itself, not only its label.
    product: line?.product || null,
    productKey: line?.productKey || '',
    productLabel: buildProductLabel(line),

    totalOrderedLength: total,
    allocatedLength: allocated,
    producedLength: produced,
    stockReservedLength: stockReserved,
    remainingLength: remaining,

    planTotal,
    progress,

    allocationStatus: allocStatus,
    allocationBadge: allocBadge,
    allocationPct,
    productionBadge: prodBadge,
    fulfillmentPct,
    readyDrums,
    plannedDrums,
    jobsCount,

    deliverySummary: String(line?.deliveryPlan?.summary || ''),
    dpItems
  };
};

const getColumns = ({ onEditItem }) => [
  {
    accessorKey: 'productLabel',
    header: 'Product',
    cell: ({ row: { original } }) => (
      <div className="d-flex flex-column">
        <Link
          to="#!"
          className="fw-semi-bold line-clamp-2 text-decoration-none"
        >
          {original.productLabel}
        </Link>
        <span className="text-muted fs-10">
          Line: {String(original.lineId).slice(0, 8)}… • Key:{' '}
          {original.productKey || '—'}
        </span>
      </div>
    ),
    meta: { headerProps: { style: { minWidth: 420 } } }
  },
  {
    id: 'alloc',
    header: 'Allocation',
    cell: ({ row: { original } }) => (
      <div className="d-flex flex-column align-items-center gap-1">
        <Badge
          bg={original.allocationBadge.color}
          className="fs-10 rounded-pill w-100 py-2 text-center"
        >
          {original.allocationBadge.label}
        </Badge>
        {/* Its own percentage, in its own column. Beside a production badge
            reading "Allocated · 0% made" a bare word invited the two to be read
            as one number, and a line with ten of seventeen thousand metres
            allocated looked like nothing had been allocated at all. */}
        <span className="text-muted fs-10">
          {fmt(original.allocatedLength)} / {fmt(original.totalOrderedLength)} m
        </span>
        <span className="text-muted fs-10">
          {original.allocationPct}% allocated · {fmt(original.jobsCount)} job(s)
        </span>
      </div>
    ),
    meta: {
      headerProps: { style: { width: 170 }, className: 'text-center' },
      cellProps: { className: 'text-center' }
    }
  },
  {
    id: 'production',
    header: 'Production',
    cell: ({ row: { original } }) => (
      <div className="d-flex flex-column align-items-center gap-1">
        <Badge
          bg={original.productionBadge.color}
          className="fs-10 rounded-pill w-100 py-2 text-center"
        >
          {original.productionBadge.label} · {original.fulfillmentPct}% made
        </Badge>
        <span className="text-muted fs-10">
          produced {fmt(original.producedLength)} /{' '}
          {fmt(original.totalOrderedLength)} m
        </span>
        {original.plannedDrums > 0 && (
          <span className="text-muted fs-10">
            {fmt(original.readyDrums)}/{fmt(original.plannedDrums)} units ready
          </span>
        )}
      </div>
    ),
    meta: {
      headerProps: { style: { width: 190 }, className: 'text-center' },
      cellProps: { className: 'text-center' }
    }
  },
  {
    accessorKey: 'totalOrderedLength',
    header: 'Ordered (m)',
    cell: ({ row: { original } }) => (
      <span className="fw-bold">{fmt(original.totalOrderedLength)}</span>
    ),
    meta: {
      headerProps: { style: { width: 140 }, className: 'text-end' },
      cellProps: { className: 'text-end' }
    }
  },
  {
    accessorKey: 'allocatedLength',
    header: 'Allocated (m)',
    cell: ({ row: { original } }) => (
      <div className="d-flex flex-column align-items-end">
        <span className="fw-semi-bold">{fmt(original.allocatedLength)}</span>
        {Number(original.stockReservedLength) > 0 && (
          <span className="text-success fs-10">
            + {fmt(original.stockReservedLength)} m from stock
          </span>
        )}
      </div>
    ),
    meta: {
      headerProps: { style: { width: 150 }, className: 'text-end' },
      cellProps: { className: 'text-end' }
    }
  },
  {
    accessorKey: 'remainingLength',
    header: 'Remaining (m)',
    cell: ({ row: { original } }) => (
      <span className="fw-semi-bold">{fmt(original.remainingLength)}</span>
    ),
    meta: {
      headerProps: { style: { width: 150 }, className: 'text-end' },
      cellProps: { className: 'text-end' }
    }
  },
  {
    id: 'deliveryPlan',
    header: 'Delivery Plan',
    cell: ({ row: { original } }) => {
      const items = safeArr(original.dpItems);
      if (!items.length) return <span className="text-muted fs-10">—</span>;

      return (
        <div className="d-flex flex-wrap gap-1">
          {items.map((item, i) => {
            const isDone = item.producedQty >= item.qty && item.qty > 0;
            // Nothing left to make and units struck off: this package was
            // cancelled. Red, so a called-off delivery cannot be mistaken for
            // one that simply has not started — they used to look identical.
            const isCancelled = item.cancelledQty > 0 && item.qty === 0;
            const isTrimmed = item.cancelledQty > 0 && item.qty > 0;

            const tone = isCancelled
              ? 'border-danger bg-danger-100'
              : isDone
                ? 'border-success bg-success-100'
                : isTrimmed
                  ? 'border-warning bg-warning-100'
                  : item.kind === 'COIL'
                    ? 'border-success bg-success-100'
                    : 'border-info bg-info-100';

            return (
              <div
                key={i}
                className={`px-2 py-1 rounded border ${tone}`}
                style={{ fontSize: 11, cursor: onEditItem ? 'pointer' : undefined }}
                role={onEditItem ? 'button' : undefined}
                // A plan typed the wrong way round used to mean cancelling the
                // order and losing every launch against it. The chip is the
                // thing somebody is already looking at when they notice.
                title={onEditItem ? 'Correct this item' : undefined}
                onClick={
                  onEditItem
                    ? () => onEditItem({ lineId: original.lineId, item: item.raw, product: original.product })
                    : undefined
                }
              >
                <div className={`fw-semibold ${isCancelled ? 'text-decoration-line-through' : ''}`}>
                  {item.specs}: {item.qty}×{fmt(item.len)}m
                </div>
                <div
                  className={
                    isCancelled ? 'text-danger' : isDone ? 'text-success' : 'text-muted'
                  }
                >
                  {isCancelled
                    ? `${item.cancelledQty} cancelled`
                    : `${item.producedQty}/${item.qty} launched`}
                  {isTrimmed && ` · ${item.cancelledQty} cancelled`}
                </div>
              </div>
            );
          })}
        </div>
      );
    },
    meta: {
      headerProps: { style: { minWidth: 240 } },
      cellProps: { className: 'py-2' }
    }
  },
  {
    id: 'progress',
    header: 'Progress',
    cell: ({ row: { original } }) => (
      <div className="d-flex flex-column align-items-center w-100">
        <ProgressBar
          now={original.progress}
          label={`${original.progress}%`}
          style={{
            height: '18px',
            width: '90%',
            fontSize: '12px',
            fontWeight: 'bold'
          }}
        />
        {!!original.deliverySummary && (
          <span
            className="text-muted fs-10 mt-1 line-clamp-1"
            title={original.deliverySummary}
          >
            {original.deliverySummary}
          </span>
        )}
      </div>
    ),
    meta: {
      headerProps: { style: { width: 200 }, className: 'text-center' },
      cellProps: { className: 'text-center' }
    }
  }
];

const OrderDetailsTable = ({ order, onChanged }) => {
  const lines = safeArr(order?.lines);
  const [editing, setEditing] = useState(null);

  const tableData = useMemo(() => lines.map(buildLineRow), [lines]);

  // Summed from the ROWS, not from the raw lines. A footer that recomputes what
  // the column above it already worked out is free to disagree with it, and did:
  // every row read 17,000 m remaining while the total underneath read 0.
  const totals = useMemo(() => {
    return tableData.reduce(
      (acc, r) => {
        acc.ordered += r.totalOrderedLength;
        acc.allocated += r.allocatedLength;
        acc.produced += r.producedLength;
        acc.stock += r.stockReservedLength;
        acc.remaining += r.remainingLength;
        acc.planned += r.planTotal;
        return acc;
      },
      { ordered: 0, allocated: 0, produced: 0, stock: 0, remaining: 0, planned: 0 }
    );
  }, [tableData]);

  const table = useAdvanceTable({
    data: tableData,
    columns: getColumns({ onEditItem: setEditing }),
    pageSize: 8,
    pagination: true,
    sortable: true
  });

  return (
    <div>
      <AdvanceTableProvider {...table}>
        <div className="border-y">
          <AdvanceTable tableProps={{ className: 'phoenix-table fs-9' }} />

          <div className="d-flex flex-between-center py-3">
            <p className="text-1100 fw-semi-bold lh-sm mb-0">Totals :</p>
            <p className="text-1100 fw-bold lh-sm mb-0">
              ordered {fmt(totals.ordered)} m
              <span className="text-700 fw-semi-bold ms-3">
                allocated {fmt(totals.allocated)} m
              </span>
              <span className="text-700 fw-semi-bold ms-3">
                produced {fmt(totals.produced)} m
              </span>
              {totals.stock > 0 && (
                <span className="text-success fw-semi-bold ms-3">
                  from stock {fmt(totals.stock)} m
                </span>
              )}
              <span className="text-700 fw-semi-bold ms-3">
                remaining {fmt(totals.remaining)} m
              </span>
              <span className="text-700 fw-semi-bold ms-3">
                plan {fmt(totals.planned)} m
              </span>
              <span className="text-1100 fw-bold ms-3">
                fulfillment{' '}
                {totals.ordered > 0
                  ? Math.min(
                      100,
                      Math.round(
                        ((totals.produced + totals.stock) / totals.ordered) * 100
                      )
                    )
                  : 0}
                %
              </span>
            </p>
          </div>
        </div>
      </AdvanceTableProvider>

      <EditPlanItemModal
        show={!!editing}
        onHide={() => setEditing(null)}
        orderId={order?.orderId}
        lineId={editing?.lineId}
        item={editing?.item}
        product={editing?.product}
        // The edit can move promises on products nobody on this page is
        // looking at, so the order has to be re-read rather than patched.
        onSaved={() => {
          setEditing(null);
          onChanged?.();
        }}
      />
    </div>
  );
};

export default OrderDetailsTable;
